import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, RotateCcw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import { useAuth } from "@/contexts/AuthContext";
import { JobPartAPI, PartAPI, extractError } from "@/lib/api";

const inr = (m) => `₹${(Math.round((m || 0) / 100)).toLocaleString("en-IN")}`;

export function JobPartsPanel({ jobId, jobStatus }) {
    const { currentBranchId, permissions } = useAuth();
    const canConsume = permissions?.includes("jobcard:consume_parts");
    const canReturn = permissions?.includes("jobcard:return_parts");
    const locked = jobStatus === "delivered" || jobStatus === "cancelled";

    const [data, setData] = useState({ items: [], net_quantity: 0, subtotal_minor: 0 });
    const [loading, setLoading] = useState(true);
    const [pickerOpen, setPickerOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await JobPartAPI.list(jobId));
        } finally { setLoading(false); }
    }, [jobId]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid="job-parts-panel">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-sm text-zinc-500">
                        {loading ? "…" : `${data.items.length} line(s) · net ${data.net_quantity} units · ${inr(data.subtotal_minor)}`}
                    </div>
                </div>
                {canConsume && !locked && (
                    <Button onClick={() => setPickerOpen(true)} data-testid="job-parts-add-btn"><Plus className="mr-1 h-4 w-4" />Add part</Button>
                )}
            </div>

            <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table data-testid="job-parts-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Part</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Returned</TableHead>
                            <TableHead className="text-right">Unit cost</TableHead>
                            <TableHead className="text-right">Line total</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {data.items.map((l) => {
                            const net = Math.max(0, Number(l.quantity) - Number(l.returned_quantity || 0));
                            const fullyReturned = net === 0;
                            return (
                                <TableRow key={l.line_id} data-testid={`job-part-row-${l.line_id}`} className={fullyReturned ? "line-through opacity-60" : ""}>
                                    <TableCell>
                                        <div className="font-mono text-xs text-zinc-500">{l.part_number}</div>
                                        <div className="font-medium">{l.part_name}</div>
                                    </TableCell>
                                    <TableCell className="text-right font-mono">{l.quantity} {l.unit_of_measure}</TableCell>
                                    <TableCell className="text-right font-mono">{l.returned_quantity || 0}</TableCell>
                                    <TableCell className="text-right font-mono">{inr(l.unit_cost_minor_at_consumption)}</TableCell>
                                    <TableCell className="text-right font-mono">{inr(net * (l.unit_cost_minor_at_consumption || 0))}</TableCell>
                                    <TableCell className="text-right">
                                        {canReturn && !locked && !fullyReturned ? (
                                            <ReturnButton jobId={jobId} line={l} onDone={load} />
                                        ) : (
                                            <Badge variant="outline" className="font-mono text-[10px]">
                                                {fullyReturned ? "returned" : (locked ? "locked" : "—")}
                                            </Badge>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {data.items.length === 0 && (
                            <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-zinc-500">No parts consumed yet.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <PartPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} jobId={jobId} branchId={currentBranchId} onDone={load} />
        </div>
    );
}

function ReturnButton({ jobId, line, onDone }) {
    const [open, setOpen] = useState(false);
    const [qty, setQty] = useState(1);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const max = Math.max(0, Number(line.quantity) - Number(line.returned_quantity || 0));
    useEffect(() => { if (open) { setQty(max); setReason(""); } }, [open, max]);
    return (
        <>
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`parts-return-btn-${line.line_id}`}>
                <RotateCcw className="mr-1 h-3 w-3" />Return
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Return {line.part_number}</DialogTitle>
                        <DialogDescription>Up to {max} {line.unit_of_measure} can be returned. Stock goes back to on-hand.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label>Quantity to return</Label>
                            <Input type="number" min={1} max={max} value={qty} onChange={(e) => setQty(Number(e.target.value))} data-testid="parts-return-qty" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Reason</Label>
                            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Customer declined the part" data-testid="parts-return-reason" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button
                            disabled={submitting || qty < 1 || qty > max}
                            data-testid="parts-return-submit"
                            onClick={async () => {
                                setSubmitting(true);
                                try {
                                    await JobPartAPI.returnLine(jobId, line.line_id, { quantity: qty, reason: reason || null });
                                    toast.success(`Returned ${qty} ${line.unit_of_measure}`);
                                    setOpen(false);
                                    onDone && onDone();
                                } catch (e) {
                                    toast.error(extractError(e));
                                } finally { setSubmitting(false); }
                            }}
                        >Confirm return</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function PartPickerDialog({ open, onOpenChange, jobId, branchId, onDone }) {
    const [q, setQ] = useState("");
    const [parts, setParts] = useState([]);
    const [selected, setSelected] = useState(null);
    const [qty, setQty] = useState(1);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        PartAPI.list({ q: q || undefined, branch_id: branchId, page_size: 50 })
            .then((d) => setParts(d.items || []));
    }, [open, q, branchId]);

    useEffect(() => { if (!open) { setSelected(null); setQ(""); setQty(1); } }, [open]);

    const submit = async () => {
        if (!selected) return;
        setSubmitting(true);
        try {
            await JobPartAPI.consume(jobId, { part_id: selected.id, quantity: Number(qty) });
            toast.success(`Picked ${qty} ${selected.unit_of_measure} of ${selected.part_number}`);
            onOpenChange(false);
            onDone && onDone();
        } catch (e) {
            toast.error(extractError(e));
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-testid="parts-picker">
                <DialogHeader>
                    <DialogTitle>Pick a part</DialogTitle>
                    <DialogDescription>Live on-hand from this branch. Stock is decremented atomically when you confirm.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search part number or name…" className="pl-9" data-testid="parts-picker-search" />
                    </div>
                    <div className="max-h-[280px] overflow-y-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                        {parts.map((p) => {
                            const oh = p.branch_stock?.quantity_on_hand ?? 0;
                            const out = oh <= 0;
                            return (
                                <button
                                    key={p.id}
                                    type="button"
                                    disabled={out}
                                    onClick={() => setSelected(p)}
                                    className={`flex w-full items-center justify-between border-b border-zinc-100 px-3 py-2 text-left text-sm last:border-0 dark:border-zinc-900 ${
                                        selected?.id === p.id ? "bg-brand-redSoft dark:bg-brand-red/10" : "hover:bg-zinc-50 dark:hover:bg-zinc-900/40"
                                    } ${out ? "cursor-not-allowed opacity-50" : ""}`}
                                    data-testid={`parts-picker-row-${p.id}`}
                                >
                                    <div>
                                        <div className="font-mono text-xs text-zinc-500">{p.part_number}</div>
                                        <div>{p.name}</div>
                                    </div>
                                    <div className="text-right font-mono text-xs">
                                        <div className={out ? "text-brand-red" : "text-emerald-600 dark:text-emerald-400"} data-testid={`part-onhand-display-${p.id}`}>{oh} {p.unit_of_measure}</div>
                                        <div className="text-zinc-500">{inr(p.unit_cost_minor)}</div>
                                    </div>
                                </button>
                            );
                        })}
                        {parts.length === 0 && <div className="py-6 text-center text-sm text-zinc-500">No matching parts.</div>}
                    </div>
                    {selected && (
                        <div className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
                            <div className="mb-2 text-xs text-zinc-500">
                                Picking <span className="font-mono">{selected.part_number}</span> — on-hand {selected.branch_stock?.quantity_on_hand ?? 0}
                            </div>
                            <div className="flex items-end gap-3">
                                <div className="flex-1 space-y-1">
                                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Quantity</Label>
                                    <Input
                                        type="number" min={1}
                                        max={selected.branch_stock?.quantity_on_hand ?? 1}
                                        value={qty} onChange={(e) => setQty(e.target.value)}
                                        data-testid="parts-picker-qty"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={!selected || submitting} data-testid="parts-picker-confirm">
                        {submitting ? "Picking…" : "Confirm"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
